import React, { lazy, createContext } from 'react'
import 'tailwindcss/tailwind.css'
import '../public/assets/custom.css'
import '../public/fontawesome/css/regular.min.css'
import '../public/fontawesome/css/solid.min.css'
import '../public/fontawesome/css/duotone.min.css'
import '../public/fontawesome/css/fontawesome.min.css'
import { AppProps, AppContext } from 'next/app'
import { Header } from '../components/header/Header'
import { appWithTranslation } from 'next-i18next'
import { Breadcrumbs } from '../components/shared/Breadcrumbs'
import { AppWidthContainer } from '../components/AppWidthContainer'
import { LocalStorageProvider } from '../components/LocalStorageProvider'
import Footer from '../components/footer/Footer'
import { Analytics } from '@vercel/analytics/react'
import { CURRENCIES } from '../lib/constants'
import { getCurrencyFromCountry } from '../lib/helper/currencyHelper'
import { LocationProvider } from '../components/context/LocationContext'

export interface SharedPageProps {
  draftMode?: boolean
  token?: string
  currencyData?: CurrencyData
}

type AppCurrency = (typeof CURRENCIES)[number]

export type CurrencyData = {
  countryCode: string
  detectedCurrency: AppCurrency
  currencyOverride: string | null
  currentCurrency: AppCurrency
}

type AppPropsWithLayout = AppProps<SharedPageProps>

// Create a context for global currency data
export const CurrencyContext = createContext<CurrencyData | null>(null)

const PreviewProvider = lazy(() => import('../components/PreviewProvider'))

function App({ Component, pageProps }: AppPropsWithLayout) {
  const { draftMode, token, currencyData } = pageProps

  return (
    <LocalStorageProvider>
      <CurrencyContext.Provider value={currencyData || null}>
        <LocationProvider>
          <Header />
          <AppWidthContainer>
            <Breadcrumbs />
            {draftMode ? (
              <PreviewProvider token={token}>
                <Component {...pageProps} />
              </PreviewProvider>
            ) : (
              <Component {...pageProps} />
            )}
          </AppWidthContainer>
          <Footer />
          <div className="h-24" />
          <Analytics />
        </LocationProvider>
      </CurrencyContext.Provider>
    </LocalStorageProvider>
  )
}

// Store reference to the original getInitialProps if it exists
const originalGetInitialProps = App.getInitialProps

// Type for the return value of getInitialProps
type AppInitialProps = {
  pageProps: {
    [key: string]: any
  }
}

// Wrapper function to modify App.getInitialProps
App.getInitialProps = async (
  appContext: AppContext,
): Promise<AppInitialProps> => {
  // Get existing props from getInitialProps if any
  const appProps: AppInitialProps = await (originalGetInitialProps
    ? originalGetInitialProps(appContext)
    : { pageProps: {} })

  // Read cookies from the request
  const { ctx } = appContext
  const req = ctx.req

  if (!req || !req.headers.cookie) {
    return appProps
  }

  const cookies = req.headers.cookie
    .split(';')
    .reduce((acc: Record<string, string>, cookie: string) => {
      const [key, value] = cookie.trim().split('=')
      if (key && value) {
        acc[key] = decodeURIComponent(value)
      }
      return acc
    }, {})

  // Get country from cookie or default to DE
  const countryCode = cookies['country'] || 'DE'

  // Get currency override from cookie or default to null
  const currencyOverride = cookies['currency_override'] || null

  // Determine the actual currency to use
  const detectedCurrency = getCurrencyFromCountry(countryCode)

  // Use override if it exists and is valid, otherwise use detected currency
  const isValidOverride =
    currencyOverride &&
    currencyOverride !== 'detect' &&
    CURRENCIES.includes(currencyOverride as AppCurrency)

  const currentCurrency = isValidOverride
    ? (currencyOverride as AppCurrency)
    : detectedCurrency

  // Merge with existing pageProps
  return {
    ...appProps,
    pageProps: {
      ...appProps.pageProps,
      currencyData: {
        countryCode,
        detectedCurrency,
        currencyOverride,
        currentCurrency,
      },
    },
  }
}

export default appWithTranslation(App)
